import { z } from 'zod'

import type { SessionDTO } from '../api/models'
import { SessionApi } from '../api'
import { hasHttpStatus } from '../utils/parseApiError'
import { addBreadcrumb } from '../utils/sentry'
import { getApiConfig } from './api-config'
import { nullableStringSchema, requiredDateSchema } from './dal-utils'
import { questionnaireDataInputSchema } from './questionnaire-data'

const getSessionApi = () => new SessionApi(getApiConfig())

const sessionDefinitionSummarySchema = z.object({
  id: z.number(),
  name: z.string().min(1),
})

/** Legacy backend value; normalized to `Aborted` so list parsing stays resilient. */
const sessionStatusSchema = z
  .enum(['PendingAnalysis', 'Aborted', 'Active', 'Completed', 'UploadFailed'])
  .transform((status) => (status === 'UploadFailed' ? 'Aborted' : status))

export type SessionStatus = z.infer<typeof sessionStatusSchema>

export const sessionAnalysisFieldsSchema = z.object({
  score: z.number().nonnegative(),
  pitchMin: nullableStringSchema,
  pitchMax: nullableStringSchema,
  comfortableMin: nullableStringSchema,
  comfortableMax: nullableStringSchema,
})

export type SessionAnalysisFields = z.infer<typeof sessionAnalysisFieldsSchema>
export type SessionAnalysisFieldsInput = z.input<
  typeof sessionAnalysisFieldsSchema
>

export const sessionSummarySchema = z.object({
  id: z.number(),
  definition: sessionDefinitionSummarySchema,
  status: sessionStatusSchema,
  created: requiredDateSchema,
})

export type SessionSummary = z.infer<typeof sessionSummarySchema>

const sessionMeasurementSummarySchema = z.object({
  id: z.number().min(0),
  definition: z.object({
    id: z.number().min(0),
    name: z.string().min(1),
  }),
})

export const sessionDetailsSchema = sessionSummarySchema
  .extend({
    analysis: sessionAnalysisFieldsSchema.nullish(),
    measurements: z
      .array(sessionMeasurementSummarySchema)
      .nullish()
      .transform((value) => value ?? []),
  })
  .transform(({ analysis, ...rest }) => ({
    ...rest,
    ...analysis,
  }))

export type SessionDetails = z.infer<typeof sessionDetailsSchema>
export type SessionDetailsInput = z.input<typeof sessionDetailsSchema>

const startSessionResultSchema = z.object({
  sessionId: z.number(),
})

export interface EndSessionParams {
  id: number
  comment: string
}

const questionTypeSchema = z.enum(['SingleChoice', 'MultipleChoice'])

export const questionDefinitionSchema = z.object({
  id: z.number(),
  title: nullableStringSchema,
  questionType: questionTypeSchema,
  minLabel: nullableStringSchema.optional(),
  maxLabel: nullableStringSchema.optional(),
  options: z
    .array(z.string())
    .nullish()
    .transform((value) => value ?? []),
})

export type QuestionDefinition = z.infer<typeof questionDefinitionSchema>

export const measurementDefinitionDetailSchema = z.object({
  id: z.number(),
  name: z.string().min(1),
  audioTrack: nullableStringSchema,
  recordingInstructions: nullableStringSchema,
  questions: z
    .array(questionDefinitionSchema)
    .nullish()
    .transform((value) => value ?? []),
})

export type MeasurementDefinitionDetail = z.infer<
  typeof measurementDefinitionDetailSchema
>

export const sessionDefinitionDetailSchema = z.object({
  id: z.number(),
  name: z.string().min(1),
  timeEstimate: z
    .number()
    .int()
    .positive()
    .nullish()
    .transform((value) => value ?? null),
  data: questionnaireDataInputSchema,
  measurementDefinitions: z
    .array(measurementDefinitionDetailSchema)
    .nullish()
    .transform((value) => value ?? []),
})

export async function fetchSessionById(id: number) {
  const api = getSessionApi()
  const dto = await api.apiSessionIdGet({ id })

  return sessionDetailsSchema.parse(dto)
}

export async function fetchSessions() {
  const api = getSessionApi()
  try {
    const dtos = await api.apiSessionGet()
    return z.array(sessionSummarySchema).parse(dtos)
  } catch (error) {
    // New accounts can reach the dashboard from RevenueCat before backend
    // entitlement/onboarding is ready. GET /api/session then 403s with an
    // empty body; treat that as "no sessions" instead of a failed query.
    if (hasHttpStatus(error, 403)) {
      addBreadcrumb('api', 'sessions_forbidden', { status: 403 }, 'warning')
      return []
    }
    throw error
  }
}

export async function fetchSessionDefinition(id: number, languageCode: string) {
  const api = getSessionApi()
  const dto = await api.apiSessionDefinitionIdGet({ id, languageCode })

  return sessionDefinitionDetailSchema.parse(dto)
}

export async function startSession(definitionId: number) {
  const api = getSessionApi()
  const dto = await api.apiSessionStartPost({ definitionId })

  return startSessionResultSchema.parse(dto)
}

export async function endSession({ id, comment }: EndSessionParams) {
  const api = getSessionApi()
  const sessionDTO: SessionDTO = { comment }

  await api.apiSessionIdEndPost({ id, sessionDTO })
}

export async function abortSession(id: number) {
  const api = getSessionApi()
  await api.apiSessionIdAbortPost({ id })
}
